export type PhoneVerificationStep = 'send' | 'verify';

type ErrorLike = {
  status?: number | string;
  data?: {
    code?: unknown;
    message?: unknown;
    error?: unknown;
    retryAfter?: unknown;
    retry_after?: unknown;
  };
};

const GENERIC_FALLBACK = 'Something went wrong. Please try again.';

/**
 * Backend hard-block code for the phone line-type and uniqueness gates
 * (Twilio Lookup non-mobile, Lookup failure, or a number already verified on
 * another account). Can arrive from /auth/send-verification-code and also from
 * /auth/verify-phone, where the backend re-runs the uniqueness check and maps a
 * unique-constraint violation to the same code.
 */
export const PHONE_NOT_ELIGIBLE_CODE = 'PHONE_NOT_ELIGIBLE';

export const PHONE_NOT_ELIGIBLE_MESSAGE =
  "We couldn't verify this number. Please use a mobile number that isn't already on a TradeMagic account.";

/**
 * OTP send gates (Asana 1218654091342336). All three come only from
 * /auth/send-verification-code, which is also the Resend route. The backend
 * emits them through the same AppError mechanism as PHONE_NOT_ELIGIBLE, so the
 * body is { status, code, message } (429: { error, code, message, retryAfter }).
 * Key on the top-level `code` and ignore the rest: the copy below is final.
 *
 * OTP_RATE_LIMITED also covers the per-day limits (up to 24 hours) and Twilio's
 * own max-attempts error (10 minutes), which is why its copy names no duration:
 * Eric's ruling of 2026-09-21 on the ticket replaced the original "in an hour".
 */
export const PHONE_COUNTRY_NOT_SUPPORTED_CODE = 'PHONE_COUNTRY_NOT_SUPPORTED';
export const OTP_RATE_LIMITED_CODE = 'OTP_RATE_LIMITED';
export const OTP_TEMPORARILY_UNAVAILABLE_CODE = 'OTP_TEMPORARILY_UNAVAILABLE';

export const PHONE_COUNTRY_NOT_SUPPORTED_MESSAGE =
  "We can't send verification codes to numbers in this country yet. Contact support@trademagic.gg if you think this is a mistake.";

export const OTP_RATE_LIMITED_MESSAGE = 'Too many verification attempts. Please try again later.';

export const OTP_TEMPORARILY_UNAVAILABLE_MESSAGE =
  'Phone verification is temporarily unavailable. Please try again later.';

/**
 * Codes whose copy is agreed and final. A code in this map wins over the HTTP
 * status and over any backend message, so the agreed string renders no matter
 * which step rejected or what else the body carries. Unknown codes fall through
 * to the status and message handling below.
 */
const CODE_MESSAGES: Record<string, string> = {
  [PHONE_NOT_ELIGIBLE_CODE]: PHONE_NOT_ELIGIBLE_MESSAGE,
  [PHONE_COUNTRY_NOT_SUPPORTED_CODE]: PHONE_COUNTRY_NOT_SUPPORTED_MESSAGE,
  [OTP_RATE_LIMITED_CODE]: OTP_RATE_LIMITED_MESSAGE,
  [OTP_TEMPORARILY_UNAVAILABLE_CODE]: OTP_TEMPORARILY_UNAVAILABLE_MESSAGE,
};

const formatRetryAfter = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) return 'a moment';
  if (seconds < 60) {
    const s = Math.round(seconds);
    return `${s} ${s === 1 ? 'second' : 'seconds'}`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
};

const extractRetryAfter = (data: ErrorLike['data']): string | null => {
  const raw = data?.retryAfter ?? data?.retry_after;
  if (raw == null) return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return formatRetryAfter(n);
};

const rateLimitMessage = (data: ErrorLike['data']): string => {
  const retryAfter = extractRetryAfter(data);
  return retryAfter
    ? `Too many attempts. Please try again in ${retryAfter}.`
    : 'Too many attempts. Please try again later.';
};

const stepFallback = (step: PhoneVerificationStep, status: number | undefined): string => {
  if (step === 'send') {
    if (status === 400) return 'Please enter a valid phone number including country code.';
    if (status === 409) return 'This phone number is already registered to another account.';
    return GENERIC_FALLBACK;
  }
  // verify
  if (status === 400) return 'Invalid verification code. Please check and try again.';
  if (status === 404 || status === 410) return 'Verification code has expired. Please request a new one.';
  return GENERIC_FALLBACK;
};

const asString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const getPhoneVerificationErrorMessage = (
  error: unknown,
  step: PhoneVerificationStep,
): string => {
  if (!error || typeof error !== 'object') return GENERIC_FALLBACK;

  const err = error as ErrorLike;

  // A known code wins over status and over any backend message so the agreed
  // copy renders regardless of which step rejected or what else the error body
  // carries. hasOwnProperty keeps a code like "toString" from matching.
  const code = err.data?.code;
  if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(CODE_MESSAGES, code)) {
    return CODE_MESSAGES[code];
  }

  const status = typeof err.status === 'number' ? err.status : undefined;
  const backendMessage = asString(err.data?.message) ?? asString(err.data?.error);

  if (status === 429) {
    return rateLimitMessage(err.data);
  }

  if (backendMessage) {
    return backendMessage;
  }

  return stepFallback(step, status);
};
